/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { 
  FileSpreadsheet, 
  Download, 
  X, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  Filter, 
  Calendar, 
  Building2, 
  BookOpen, 
  Search, 
  Check, 
  Sparkles,
  Layers,
  FileCheck2
} from 'lucide-react';
import { DC3Record, CostCenter } from '../types.ts';
import { exportDC3RecordsToExcel, getDC3ValidityInfo } from '../utils/dc3ExcelExporter.ts';

interface DC3ExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  dc3Records: DC3Record[];
  costCenters: CostCenter[];
  activeCostCenter?: CostCenter | null;
  initialFilterMode?: 'all' | 'expiring_30' | 'expired' | 'expiring_and_expired' | 'valid';
}

export const DC3ExcelExportModal: React.FC<DC3ExcelExportModalProps> = ({
  isOpen,
  onClose,
  dc3Records,
  costCenters,
  activeCostCenter,
  initialFilterMode = 'expiring_30'
}) => {
  // Filter States
  const [filterPreset, setFilterPreset] = useState<'all' | 'expiring_30' | 'expired' | 'expiring_and_expired' | 'valid'>(initialFilterMode);
  const [customDaysThreshold, setCustomDaysThreshold] = useState<number>(30);
  const [selectedCostCenterId, setSelectedCostCenterId] = useState<string>(activeCostCenter?.id || 'all');
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dateRangeType, setDateRangeType] = useState<'all' | 'issue' | 'expiry'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Export Formatting Options
  const [includeExecutiveSheet, setIncludeExecutiveSheet] = useState<boolean>(true);
  const [includeExpiringSheet, setIncludeExpiringSheet] = useState<boolean>(true);
  const [customReportTitle, setCustomReportTitle] = useState<string>('Matriz de Constancias DC-3 y Control de Vencimientos STPS');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);

  // Sync initial preset if modal reopens
  React.useEffect(() => {
    if (isOpen) {
      setFilterPreset(initialFilterMode);
      setExportSuccess(false);
    }
  }, [isOpen, initialFilterMode]);

  // Distinct Courses List for filter dropdown
  const uniqueCourses = useMemo(() => {
    const set = new Set<string>();
    dc3Records.forEach(r => {
      if (r.courseName && r.courseName.trim()) {
        set.add(r.courseName.trim());
      }
    });
    return Array.from(set).sort();
  }, [dc3Records]);

  // Enrich records with calculated expiration days
  const enrichedRecords = useMemo(() => {
    return dc3Records.map(r => ({
      ...r,
      validity: getDC3ValidityInfo(r.issueDate, r.expiryDate)
    }));
  }, [dc3Records]);

  // Filter evaluation logic
  const filteredRecords = useMemo(() => {
    return enrichedRecords.filter(rec => {
      // 1. Cost Center Filter
      if (selectedCostCenterId !== 'all') {
        if (rec.costCenterId !== selectedCostCenterId && rec.costCenterName !== selectedCostCenterId) {
          return false;
        }
      }

      // 2. Course Filter
      if (selectedCourse !== 'all' && rec.courseName !== selectedCourse) {
        return false;
      }

      // 3. Search Query (Name, CURP, ID, Course, Trainer)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const match = 
          (rec.employeeName && rec.employeeName.toLowerCase().includes(q)) ||
          (rec.curp && rec.curp.toLowerCase().includes(q)) ||
          (rec.employeeId && rec.employeeId.toLowerCase().includes(q)) ||
          (rec.courseName && rec.courseName.toLowerCase().includes(q)) ||
          (rec.trainer && rec.trainer.toLowerCase().includes(q)) ||
          (rec.role && rec.role.toLowerCase().includes(q));
        if (!match) return false;
      }

      // 4. Date Range Filter
      if (dateRangeType !== 'all') {
        const targetDate = dateRangeType === 'issue' ? rec.issueDate : rec.validity.expiryDate;
        if (startDate && targetDate < startDate) return false;
        if (endDate && targetDate > endDate) return false;
      }

      // 5. Preset Status Filter (The Core 30-Day Expiry logic)
      const days = rec.validity.daysRemaining;
      if (filterPreset === 'expiring_30') {
        // Próximos a vencer en <= 30 días (y que aún no hayan vencido, o que estén en ventana)
        return days >= 0 && days <= customDaysThreshold;
      }

      if (filterPreset === 'expired') {
        return days < 0;
      }

      if (filterPreset === 'expiring_and_expired') {
        return days <= customDaysThreshold;
      }

      if (filterPreset === 'valid') {
        return days > customDaysThreshold;
      }

      return true; // 'all'
    });
  }, [
    enrichedRecords,
    filterPreset,
    customDaysThreshold,
    selectedCostCenterId,
    selectedCourse,
    searchQuery,
    dateRangeType,
    startDate,
    endDate
  ]);

  // Statistics for the filtered subset
  const stats = useMemo(() => {
    const total = filteredRecords.length;
    const expiringSoon = filteredRecords.filter(r => r.validity.daysRemaining >= 0 && r.validity.daysRemaining <= customDaysThreshold).length;
    const expired = filteredRecords.filter(r => r.validity.daysRemaining < 0).length;
    const valid = filteredRecords.filter(r => r.validity.daysRemaining > customDaysThreshold).length;
    const totalHours = filteredRecords.reduce((acc, r) => acc + (Number(r.durationHours) || 0), 0);
    return { total, expiringSoon, expired, valid, totalHours };
  }, [filteredRecords, customDaysThreshold]);

  // Trigger Excel Generation
  const handleExport = async () => {
    if (filteredRecords.length === 0) {
      alert('No hay registros que coincidan con los filtros seleccionados.');
      return;
    }

    setIsExporting(true);
    setExportSuccess(false);

    try {
      let filterDesc = 'Todos los registros evaluados';
      if (filterPreset === 'expiring_30') {
        filterDesc = `Filtrado específico: Constancias próximas a vencer en ≤ ${customDaysThreshold} días para renovación oportuna`;
      } else if (filterPreset === 'expired') {
        filterDesc = 'Filtrado específico: Constancias DC-3 vencidas (+1 año de emisión)';
      } else if (filterPreset === 'expiring_and_expired') {
        filterDesc = `Filtrado integral: Constancias vencidas y próximas a vencer (≤ ${customDaysThreshold} días)`;
      } else if (filterPreset === 'valid') {
        filterDesc = `Filtrado: Constancias vigentes con más de ${customDaysThreshold} días de vigencia`;
      }

      if (selectedCostCenterId !== 'all') {
        const cc = costCenters.find(c => c.id === selectedCostCenterId);
        filterDesc += ` | Planta: ${cc ? cc.name : selectedCostCenterId}`;
      }
      if (selectedCourse !== 'all') {
        filterDesc += ` | Curso: ${selectedCourse}`;
      }

      const activeCC = costCenters.find(c => c.id === selectedCostCenterId);
      const ccName = activeCC ? activeCC.name : (selectedCostCenterId === 'all' ? 'Todas las Plantas / Sedes' : selectedCostCenterId);

      const timestamp = new Date().toISOString().split('T')[0];
      let filePrefix = 'Reporte_Formatos_DC3_STPS';
      if (filterPreset === 'expiring_30') {
        filePrefix = `DC3_Proximos_A_Vencer_${customDaysThreshold}Dias`;
      } else if (filterPreset === 'expired') {
        filePrefix = 'DC3_Formatos_Vencidos';
      }

      await exportDC3RecordsToExcel(filteredRecords, {
        fileName: `${filePrefix}_${timestamp}.xlsx`,
        reportTitle: customReportTitle,
        filterDescription: filterDesc,
        costCenterName: ccName,
        generatedBy: 'Auditor HSEQ & Comisión Mixta STPS',
        includeExecutiveSummarySheet: includeExecutiveSheet,
        includeExpiringOnlySheet: includeExpiringSheet,
        highlightExpiringDays: customDaysThreshold
      });

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
      }, 4000);
    } catch (err) {
      console.error('[DC3 Excel Export Modal] Error exporting:', err);
      alert('Ocurrió un error al generar el archivo Excel.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-[#14151a] border border-[#2d2f3d] rounded-2xl max-w-4xl w-full text-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh] my-auto">
        
        {/* MODAL HEADER */}
        <div className="p-4 sm:p-5 bg-[#1a1c24] border-b border-[#2d2f3d] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-950/80 border border-emerald-600/50 flex items-center justify-center text-emerald-400 shadow-md">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white">
                  Exportar Formatos DC-3 a Excel (STPS)
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold uppercase">
                  Logo & Formato Pro
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Filtre constancias por colaboradores, cursos, fechas y ventana de vencimiento de 30 días para renovación oportuna.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* MODAL BODY (SCROLLABLE) */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-5 text-xs flex-1">

          {/* 1. SELECCIÓN DE FILTROS PREDEFINIDOS DE VENCIMIENTO */}
          <div className="space-y-2.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 text-amber-400" />
              <span>1. Filtro Principal por Estatus de Vencimiento:</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              
              {/* Opción 1: Próximos a Vencer (30 Días) - DESTACADA */}
              <button
                type="button"
                onClick={() => setFilterPreset('expiring_30')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer relative overflow-hidden ${
                  filterPreset === 'expiring_30'
                    ? 'bg-gradient-to-br from-amber-950/80 via-amber-900/40 to-[#1e1e24] border-amber-500 ring-1 ring-amber-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-amber-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-amber-300 text-xs">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span>Próximos a Vencer (≤ {customDaysThreshold}d)</span>
                  </span>
                  {filterPreset === 'expiring_30' && (
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                  )}
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Cursos en ventana crítica de 30 días para programar renovaciones oportunas.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-amber-400 bg-amber-950/60 px-2 py-0.5 rounded inline-block border border-amber-500/40">
                  {enrichedRecords.filter(r => r.validity.daysRemaining >= 0 && r.validity.daysRemaining <= customDaysThreshold).length} Formatos detectados
                </div>
              </button>

              {/* Opción 2: Vencidos (+1 Año) */}
              <button
                type="button"
                onClick={() => setFilterPreset('expired')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  filterPreset === 'expired'
                    ? 'bg-gradient-to-br from-red-950/80 via-red-900/40 to-[#1e1e24] border-red-500 ring-1 ring-red-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-red-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-red-300 text-xs">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span>Solo Vencidos (+1 Año)</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Constancias vencidas que requieren re-capacitación urgente.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-red-400 bg-red-950/60 px-2 py-0.5 rounded inline-block border border-red-500/40">
                  {enrichedRecords.filter(r => r.validity.daysRemaining < 0).length} Formatos vencidos
                </div>
              </button>

              {/* Opción 3: Vencidos + Próximos a Vencer */}
              <button
                type="button"
                onClick={() => setFilterPreset('expiring_and_expired')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  filterPreset === 'expiring_and_expired'
                    ? 'bg-gradient-to-br from-orange-950/80 via-orange-900/40 to-[#1e1e24] border-orange-500 ring-1 ring-orange-500/50 shadow-lg'
                    : 'bg-[#181920] border-[#2c2e3d] hover:border-orange-500/40'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="flex items-center gap-1.5 font-bold text-orange-300 text-xs">
                    <Sparkles className="w-4 h-4 text-orange-400" />
                    <span>Plan Renovación Total</span>
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-snug">
                  Consolida vencidos y próximos a vencer en ≤ {customDaysThreshold} días.
                </p>
                <div className="mt-2 text-[10px] font-mono font-bold text-orange-400 bg-orange-950/60 px-2 py-0.5 rounded inline-block border border-orange-500/40">
                  {enrichedRecords.filter(r => r.validity.daysRemaining <= customDaysThreshold).length} Formatos en alerta
                </div>
              </button>
            </div>

            {/* Sub-botones para Todos o Vigentes */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setFilterPreset('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  filterPreset === 'all'
                    ? 'bg-slate-700 text-white border border-slate-500'
                    : 'bg-[#181920] text-slate-400 hover:text-white border border-[#2c2e3d]'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>Todos los Formatos ({enrichedRecords.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setFilterPreset('valid')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  filterPreset === 'valid'
                    ? 'bg-emerald-950 text-emerald-300 border border-emerald-500'
                    : 'bg-[#181920] text-slate-400 hover:text-emerald-400 border border-[#2c2e3d]'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Solo Vigentes (&gt; {customDaysThreshold}d)</span>
              </button>

              {/* Selector de ventana de días para la alerta */}
              <div className="ml-auto flex items-center gap-2 bg-[#181920] px-2.5 py-1 rounded-lg border border-[#2c2e3d]">
                <span className="text-[11px] text-slate-400">Ventana de Alerta:</span>
                {[15, 30, 60, 90].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setCustomDaysThreshold(days)}
                    className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono transition-colors ${
                      customDaysThreshold === days
                        ? 'bg-amber-500 text-black font-bold'
                        : 'text-slate-400 hover:text-white bg-[#101115]'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 2. FILTROS SECUNDARIOS (PLANTA, CURSO, BÚSQUEDA, FECHAS) */}
          <div className="p-3.5 bg-[#181920] rounded-xl border border-[#282a36] space-y-3">
            <div className="flex items-center justify-between text-slate-300 font-bold text-[11px] uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5 text-sky-400" />
                <span>2. Filtros Específicos Adicionales:</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedCostCenterId('all');
                  setSelectedCourse('all');
                  setSearchQuery('');
                  setDateRangeType('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="text-[10px] text-slate-400 hover:text-white underline cursor-pointer"
              >
                Restablecer Filtros
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              
              {/* Centro de Costos */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-emerald-400" />
                  <span>Centro de Costos / Sede:</span>
                </label>
                <select
                  value={selectedCostCenterId}
                  onChange={(e) => setSelectedCostCenterId(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="all">Todas las Sedes / Plantas</option>
                  {costCenters.map(cc => (
                    <option key={cc.id} value={cc.id}>{cc.name} ({cc.code || cc.id})</option>
                  ))}
                </select>
              </div>

              {/* Norma / Curso STPS */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <BookOpen className="w-3 h-3 text-sky-400" />
                  <span>Curso / Norma STPS:</span>
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-sky-500"
                >
                  <option value="all">Todos los Cursos ({uniqueCourses.length})</option>
                  {uniqueCourses.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Búsqueda por Colaborador */}
              <div>
                <label className="text-[10px] text-slate-400 block mb-1 font-semibold flex items-center gap-1">
                  <Search className="w-3 h-3 text-amber-400" />
                  <span>Colaborador, CURP o Nómina:</span>
                </label>
                <input
                  type="text"
                  placeholder="Ej: Mendoza, MERJ85..., Operador"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            {/* Rango de Fechas Opcional */}
            <div className="pt-2 border-t border-[#232530] flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] text-slate-400 font-semibold">Filtrar por Rango de Fechas:</span>
                <select
                  value={dateRangeType}
                  onChange={(e) => setDateRangeType(e.target.value as any)}
                  className="bg-[#101115] border border-[#2c2e3d] rounded px-2 py-1 text-[11px] text-white"
                >
                  <option value="all">Sin límite de fechas</option>
                  <option value="issue">Fecha de Emisión (Vigencia Inicial)</option>
                  <option value="expiry">Fecha de Vencimiento (1 Año)</option>
                </select>
              </div>

              {dateRangeType !== 'all' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-[#101115] border border-[#2c2e3d] rounded px-2 py-1 text-[11px] text-white"
                  />
                  <span className="text-slate-500">hasta</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-[#101115] border border-[#2c2e3d] rounded px-2 py-1 text-[11px] text-white"
                  />
                </div>
              )}
            </div>
          </div>

          {/* 3. VISTA PREVIA Y CONTADOR EN TIEMPO REAL */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <FileCheck2 className="w-3.5 h-3.5 text-emerald-400" />
                <span>3. Resumen y Vista Previa de Registros a Exportar:</span>
              </span>
              <span className="text-[11px] font-mono text-emerald-400 font-bold bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-600/40">
                {stats.total} Formatos Seleccionados
              </span>
            </div>

            {/* Mini KPI summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#101115] p-2.5 rounded-xl border border-[#262836]">
              <div className="text-center p-1.5 bg-[#181920] rounded-lg">
                <span className="text-[10px] text-slate-400 block font-semibold">Total a Exportar</span>
                <strong className="text-sm text-white font-mono">{stats.total}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-amber-500/20">
                <span className="text-[10px] text-amber-300 block font-semibold">Vencen en ≤ {customDaysThreshold}d</span>
                <strong className="text-sm text-amber-400 font-mono">{stats.expiringSoon}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-red-500/20">
                <span className="text-[10px] text-red-300 block font-semibold">Vencidos (+1a)</span>
                <strong className="text-sm text-red-400 font-mono">{stats.expired}</strong>
              </div>
              <div className="text-center p-1.5 bg-[#181920] rounded-lg border border-emerald-500/20">
                <span className="text-[10px] text-emerald-300 block font-semibold">Vigentes</span>
                <strong className="text-sm text-emerald-400 font-mono">{stats.valid}</strong>
              </div>
            </div>

            {/* Scrollable Preview Table */}
            <div className="max-h-48 overflow-y-auto overflow-x-auto rounded-xl border border-[#262836] bg-[#101115]">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-[#181922] text-slate-400 font-mono uppercase text-[9px] sticky top-0 border-b border-[#2c2e3d]">
                  <tr>
                    <th className="p-2">Colaborador</th>
                    <th className="p-2">Curso / Norma STPS</th>
                    <th className="p-2">Emisión</th>
                    <th className="p-2">Vence</th>
                    <th className="p-2">Días Rest.</th>
                    <th className="p-2">Estatus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1e2029]">
                  {filteredRecords.slice(0, 50).map((rec) => (
                    <tr key={rec.id} className="hover:bg-[#161720]">
                      <td className="p-2 font-bold text-white whitespace-nowrap">
                        {rec.employeeName}
                        <span className="text-[9px] text-slate-400 block font-normal">{rec.role} ({rec.employeeId || 'S/N'})</span>
                      </td>
                      <td className="p-2 text-slate-200">
                        {rec.courseName}
                      </td>
                      <td className="p-2 text-slate-400 font-mono whitespace-nowrap">
                        {rec.issueDate}
                      </td>
                      <td className="p-2 font-mono font-bold text-white whitespace-nowrap">
                        {rec.validity.expiryDate}
                      </td>
                      <td className="p-2 font-mono whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded font-bold ${
                          rec.validity.daysRemaining < 0
                            ? 'bg-red-950 text-red-300'
                            : rec.validity.daysRemaining <= customDaysThreshold
                            ? 'bg-amber-950 text-amber-300'
                            : 'text-emerald-400'
                        }`}>
                          {rec.validity.daysRemaining < 0 ? `${rec.validity.daysRemaining}d` : `${rec.validity.daysRemaining}d`}
                        </span>
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {rec.validity.computedStatus === 'vencido' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-950 text-red-300 border border-red-700">
                            Vencido
                          </span>
                        )}
                        {rec.validity.computedStatus === 'por_renovar' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-950 text-amber-300 border border-amber-700">
                            Por Vencer (≤{customDaysThreshold}d)
                          </span>
                        )}
                        {rec.validity.computedStatus === 'vigente' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-700">
                            Vigente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredRecords.length === 0 && (
                <div className="p-6 text-center text-slate-400">
                  No hay registros DC-3 que cumplan con los filtros seleccionados.
                </div>
              )}
            </div>
          </div>

          {/* 4. OPCIONES DE FORMATO EXCEL & MULTI-HOJA */}
          <div className="p-3.5 bg-[#181920] rounded-xl border border-[#282a36] space-y-2.5">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider block">
              4. Opciones de Estilo y Hojas del Libro Excel:
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <label className="flex items-center gap-2 p-2 rounded-lg bg-[#101115] border border-[#232530] cursor-pointer hover:border-slate-600">
                <input
                  type="checkbox"
                  checked={includeExpiringSheet}
                  onChange={(e) => setIncludeExpiringSheet(e.target.checked)}
                  className="rounded text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-slate-200">
                  Incluir Hoja Especial <strong className="text-amber-300">"Alertas de Renovación (30 Días)"</strong>
                </span>
              </label>

              <label className="flex items-center gap-2 p-2 rounded-lg bg-[#101115] border border-[#232530] cursor-pointer hover:border-slate-600">
                <input
                  type="checkbox"
                  checked={includeExecutiveSheet}
                  onChange={(e) => setIncludeExecutiveSheet(e.target.checked)}
                  className="rounded text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-slate-200">
                  Incluir Hoja de <strong className="text-sky-300">"Resumen Ejecutivo por Norma STPS"</strong>
                </span>
              </label>
            </div>

            <div className="text-[11px] text-slate-400 bg-emerald-950/20 border border-emerald-500/30 p-2 rounded-lg flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>
                El archivo generado incluirá automáticamente el <strong>Logotipo Institucional</strong>, encabezados estilizados en alta definición, márgenes de impresión, tarjetas de KPI y bloque de firmas de la <strong>Comisión Mixta STPS</strong>.
              </span>
            </div>
          </div>

        </div>

        {/* MODAL FOOTER */}
        <div className="p-4 bg-[#1a1c24] border-t border-[#2d2f3d] flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {exportSuccess && (
              <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5 animate-pulse">
                <CheckCircle2 className="w-4 h-4" />
                ¡Reporte Excel descargado exitosamente!
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-[#252836] hover:bg-slate-700 text-slate-300 text-xs font-semibold cursor-pointer transition-colors"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting || filteredRecords.length === 0}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-emerald-950/50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Generando Libro Excel...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 text-emerald-200" />
                  <span>Descargar Excel Profesional ({stats.total})</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
